import type { TranslationKey } from './index';

/**
 * Bengali (বাংলা). Typed as `Record<TranslationKey, string>` on purpose: adding a
 * key to `en.ts` without one here is a compile error, which is the only reliable
 * way to keep a second language from quietly rotting.
 *
 * Platform names (Facebook, Instagram, …) stay in Latin script because that is
 * how they are written and searched for in Bengali usage — transliterating them
 * would make them harder to recognise, not easier.
 *
 * Note this dictionary controls the *interface* only. What language a post is
 * generated in comes from the brand profile, so switching to Bengali here never
 * changes what goes out to an audience.
 */
export const bn: Record<TranslationKey, string> = {
  // ─── Shared ────────────────────────────────────────────────────────────────
  'common.appName': 'সোশ্যাল কনটেন্ট স্টুডিও',
  'common.appTagline': 'সব চ্যানেলে কনটেন্ট পরিকল্পনা, তৈরি ও প্রকাশ',
  'common.brand': 'সোশ্যাল স্টুডিও',
  'common.back': 'ফিরে যান',
  'common.dashboard': 'ড্যাশবোর্ড',
  'common.previous': 'পূর্ববর্তী',
  'common.continue': 'পরবর্তী',
  'common.loading': 'লোড হচ্ছে…',
  'common.processing': 'প্রক্রিয়াধীন…',
  'common.cancel': 'বাতিল',
  'common.confirm': 'নিশ্চিত করুন',
  'common.edit': 'সম্পাদনা',
  'common.done': 'সম্পন্ন',
  'common.view': 'দেখুন',
  'common.prev': 'পূর্ব',
  'common.next': 'পরবর্তী',
  'common.signIn': 'সাইন ইন',
  'common.signOut': 'সাইন আউট',
  'common.expired': 'মেয়াদোত্তীর্ণ',
  'common.emptyValue': '—',

  // ─── Language toggle ───────────────────────────────────────────────────────
  'language.switchTo': '{language} এ পরিবর্তন করুন',

  // ─── Payment / subscription statuses ───────────────────────────────────────
  'status.pending': 'অপেক্ষমাণ',
  'status.completed': 'সম্পন্ন',
  'status.failed': 'ব্যর্থ',
  'status.cancelled': 'বাতিল',
  'status.active': 'সক্রিয়',
  'status.expired': 'মেয়াদোত্তীর্ণ',

  // ─── Platforms ─────────────────────────────────────────────────────────────
  'platform.facebook': 'Facebook',
  'platform.instagram': 'Instagram',
  'platform.linkedin': 'LinkedIn',
  'platform.tiktok': 'TikTok',
  'platform.youtube': 'YouTube',

  // ─── Industry verticals ────────────────────────────────────────────────────
  'vertical.restaurant': 'রেস্তোরাঁ ও ক্যাফে',
  'vertical.dental': 'ডেন্টাল ক্লিনিক',
  'vertical.real_estate': 'রিয়েল এস্টেট',
  'vertical.cleaning': 'ক্লিনিং কোম্পানি',
  'vertical.agency': 'এজেন্সি',
  'vertical.other': 'অন্যান্য',

  // ─── Post lifecycle ────────────────────────────────────────────────────────
  'postStatus.draft': 'খসড়া',
  'postStatus.pending_review': 'পর্যালোচনার অপেক্ষায়',
  'postStatus.approved': 'অনুমোদিত',
  'postStatus.scheduled': 'নির্ধারিত',
  'postStatus.publishing': 'প্রকাশ হচ্ছে…',
  'postStatus.published': 'প্রকাশিত',
  'postStatus.failed': 'ব্যর্থ',
  'postStatus.cancelled': 'বাতিল',

  // ─── Login ─────────────────────────────────────────────────────────────────
  'login.title': 'সোশ্যাল কনটেন্ট স্টুডিও',
  'login.subtitle': 'AI কনটেন্ট — নির্ধারিত সময়ে স্বয়ংক্রিয়ভাবে প্রকাশিত',
  'login.welcome': 'স্বাগতম',
  'login.description': 'আপনার কনটেন্ট ও সংযুক্ত অ্যাকাউন্ট পরিচালনা করতে সাইন ইন করুন।',
  'login.google': 'Google দিয়ে চালিয়ে যান',
  'login.terms':
    'সাইন ইন করার মাধ্যমে আপনি আমাদের সেবার শর্তাবলীতে সম্মত হচ্ছেন। নতুন ব্যবহারকারীরা ১৪ দিনের ফ্রি ট্রায়াল পান।',
  'login.suspendedTitle': 'এই অ্যাকাউন্টটি স্থগিত',
  'login.suspendedBody':
    'স্থগিতাদেশ বলবৎ থাকা অবস্থায় সাইন ইন বন্ধ থাকবে। ভুল হয়েছে মনে করলে সাপোর্টে যোগাযোগ করুন।',
  'login.failedTitle': 'সাইন ইন ব্যর্থ হয়েছে',

  // ─── Session expiry ────────────────────────────────────────────────────────
  'session.title': 'আপনার সেশনের মেয়াদ শেষ',
  'session.description':
    'নিরাপত্তার জন্য আপনাকে সাইন আউট করা হয়েছে। চালিয়ে যেতে আবার লগ ইন করুন — সংরক্ষণ না করা পরিবর্তনগুলো আবার করতে হবে।',
  'session.logout': 'লগ আউট',

  // ─── Account suspension ────────────────────────────────────────────────────
  'suspended.title': 'আপনার অ্যাকাউন্ট স্থগিত',
  'suspended.description':
    'একজন প্রশাসক এই অ্যাকাউন্টটি স্থগিত করেছেন, তাই স্থগিতাদেশ শেষ না হওয়া পর্যন্ত এটি ব্যবহার করা যাবে না। পুনরায় সাইন ইন করলেও অ্যাক্সেস ফিরে আসবে না।',
  'suspended.descriptionIndefinite':
    'একজন প্রশাসক এই অ্যাকাউন্টটি অনির্দিষ্টকালের জন্য স্থগিত করেছেন। পুনরায় সাইন ইন করলেও অ্যাক্সেস ফিরে আসবে না — ভুল হয়েছে মনে করলে সাপোর্টে যোগাযোগ করুন।',
  'suspended.reasonLabel': 'কারণ',
  'suspended.untilLabel': 'স্থগিত থাকবে',
  'suspended.indefinite': 'অনির্দিষ্টকাল',
  'suspended.logout': 'লগ আউট',

  // ─── Dashboard navigation ──────────────────────────────────────────────────
  'nav.content': 'কনটেন্ট',
  'nav.setup': 'সেটআপ',
  'nav.analytics': 'বিশ্লেষণ',
  'nav.account': 'অ্যাকাউন্ট',
  'nav.overview': 'সংক্ষিপ্ত বিবরণ',
  'nav.calendar': 'ক্যালেন্ডার',
  'nav.composer': 'কম্পোজার',
  'nav.approvals': 'অনুমোদন',
  'nav.library': 'মিডিয়া লাইব্রেরি',
  'nav.brands': 'ব্র্যান্ড',
  'nav.accounts': 'সংযুক্ত অ্যাকাউন্ট',
  'nav.subscription': 'সাবস্ক্রিপশন',
  'nav.payments': 'পেমেন্ট ইতিহাস',
  'nav.admin': 'প্রশাসন',
  'nav.adminUsers': 'ব্যবহারকারী',
  'nav.adminActivity': 'ব্যবহারকারীর কার্যক্রম',
  'nav.adminStats': 'পরিসংখ্যান',
  'nav.collapse': 'প্যানেল সংকুচিত করুন',
  'nav.resize': 'প্যানেলের আকার পরিবর্তন — অ্যারো কী ব্যবহার করুন',
  'nav.dragResize': 'টেনে আকার পরিবর্তন করুন',
  'nav.breadcrumb': 'হোম / ড্যাশবোর্ড',

  // ─── Dashboard: overview ───────────────────────────────────────────────────
  'dashboard.heroTitle': 'আপনার পুরো কনটেন্ট ক্যালেন্ডার — লেখা ও নির্ধারিত 📣',
  'dashboard.heroBody':
    'একবার আপনার ব্যবসার বর্ণনা দিন। প্রতিটি প্ল্যাটফর্মের জন্য পোস্ট তৈরি করুন, পর্যালোচনা করুন, আর সেগুলো নিজে থেকেই প্রকাশ হবে।',
  'dashboard.heroCta': 'আপনার ব্র্যান্ড সেট আপ করুন',
  'dashboard.postsThisMonth': 'প্রকাশিত পোস্ট',
  'dashboard.generationsThisMonth': 'AI জেনারেশন',
  'dashboard.ofLimit': 'এ মাসে {limit} টির মধ্যে',
  'dashboard.getStarted': 'শুরু করুন',
  'dashboard.stepBrandTitle': 'আপনার ব্যবসার বর্ণনা দিন',
  'dashboard.stepBrandBody':
    'ধরন, সেবা, ভঙ্গি ও দর্শক। প্রতিটি পোস্ট এখান থেকেই লেখা হয়।',
  'dashboard.stepConnectTitle': 'আপনার অ্যাকাউন্ট সংযুক্ত করুন',
  'dashboard.stepConnectBody': 'যে পেজ ও প্রোফাইলে প্রকাশ করতে চান সেগুলো যুক্ত করুন।',
  'dashboard.stepComposeTitle': 'একটি পোস্ট তৈরি করুন',
  'dashboard.stepComposeBody': 'একটি ভাবনা থেকে প্রতিটি প্ল্যাটফর্মের উপযোগী আলাদা সংস্করণ।',
  'dashboard.stepScheduleTitle': 'ক্যালেন্ডার পূর্ণ করুন',
  'dashboard.stepScheduleBody': 'সময় নির্ধারণ করুন, নির্ধারিত পোস্ট নিজে থেকেই প্রকাশ হবে।',

  // ─── Not-yet-built tabs ────────────────────────────────────────────────────
  'placeholder.calendarTitle': 'ক্যালেন্ডার শীঘ্রই আসছে',
  'placeholder.calendarBody':
    'প্রকাশনা পাইপলাইনের সাথে সময় নির্ধারণ যুক্ত হবে। আগে আসবে সংযুক্ত অ্যাকাউন্ট ও কনটেন্ট তৈরি।',
  'placeholder.composerTitle': 'কম্পোজার শীঘ্রই আসছে',
  'placeholder.composerBody':
    'ব্র্যান্ড প্রোফাইল তৈরি হওয়ার পর পরবর্তী ধাপে AI জেনারেশন যুক্ত হবে।',
  'placeholder.brandsTitle': 'ব্র্যান্ড সেটআপ শীঘ্রই আসছে',
  'placeholder.brandsBody':
    'এটিই প্রথমে তৈরি হচ্ছে — যে ব্যবসায়িক প্রোফাইল থেকে প্রতিটি পোস্ট লেখা হবে।',
  'placeholder.accountsTitle': 'অ্যাকাউন্ট সংযোগ শীঘ্রই আসছে',
  'placeholder.accountsBody':
    'Facebook, Instagram, LinkedIn, TikTok ও YouTube সংযোগ প্ল্যাটফর্ম পর্যালোচনার উপর নির্ভরশীল, যা ইতিমধ্যে চলছে।',
  'placeholder.libraryTitle': 'মিডিয়া লাইব্রেরি শীঘ্রই আসছে',
  'placeholder.libraryBody': 'মিডিয়া পাইপলাইনের সাথে ছবি ও ভিডিও আপলোড যুক্ত হবে।',
  'placeholder.approvalsTitle': 'অনুমোদন শীঘ্রই আসছে',
  'placeholder.approvalsBody':
    'সময় নির্ধারণের সাথে প্রকাশের আগে পর্যালোচনা যুক্ত হবে। নিয়ন্ত্রিত খাতের জন্য এটি ডিফল্টে চালু থাকবে।',
  'placeholder.analyticsTitle': 'বিশ্লেষণ শীঘ্রই আসছে',
  'placeholder.analyticsBody':
    'পোস্ট প্রকাশ শুরু হলে ও পরিমাপযোগ্য তথ্য জমা হলে প্রতি পোস্টের ফলাফল দেখা যাবে।',

  // ─── Dashboard: subscription tab ───────────────────────────────────────────
  'subscription.signInPrompt': 'সাবস্ক্রিপশন পরিচালনা করতে সাইন ইন করুন।',
  'subscription.loadError': 'সাবস্ক্রিপশন লোড করা যায়নি।',
  'subscription.planLabel': '{plan} প্ল্যান',
  'subscription.daysLeft': '{days} দিন বাকি',
  'subscription.daysShort': '{days}দি',
  'subscription.days': '{days} দিন',
  'subscription.postsUsed': 'প্রকাশিত পোস্ট',
  'subscription.generationsUsed': 'AI জেনারেশন',
  'subscription.storageUsed': 'মিডিয়া স্টোরেজ',
  'subscription.expiryWarning': '{days} দিনে মেয়াদ শেষ। শীঘ্রই নবায়ন করুন।',
  'subscription.expiryWarningOne': '১ দিনে মেয়াদ শেষ। শীঘ্রই নবায়ন করুন।',
  'subscription.viewUpgrade': 'প্ল্যান দেখুন / আপগ্রেড করুন',
  'subscription.renewalTitle': 'আপনার সাবস্ক্রিপশনের মেয়াদ শেষ',
  'subscription.renewalBody': 'প্রকাশনা চালিয়ে যেতে আবার সাবস্ক্রাইব করুন।',
  'subscription.renewalAction': 'প্ল্যান দেখুন',
  'subscription.renewalDismiss': 'পরে মনে করিয়ে দিন',
  'subscription.cancelAction': 'সাবস্ক্রিপশন বাতিল করুন',
  'subscription.cancelling': 'বাতিল হচ্ছে…',
  'subscription.cancelFailed': 'বাতিল করা যায়নি। আবার চেষ্টা করুন।',
  'subscription.confirmTitle': 'সাবস্ক্রিপশন বাতিল করবেন?',
  'subscription.confirmBody':
    'বর্তমান মেয়াদ শেষ না হওয়া পর্যন্ত অ্যাক্সেস থাকবে। ওই তারিখের পরে নির্ধারিত পোস্টগুলো প্রকাশ হবে না।',
  'subscription.confirmYes': 'সাবস্ক্রিপশন বাতিল করুন',
  'subscription.confirmNo': 'রেখে দিন',
  'subscription.limitsTitle': 'আপনার প্ল্যানের সীমা',
  'subscription.limitBrands': 'ব্র্যান্ড:',
  'subscription.limitAccounts': 'সংযুক্ত অ্যাকাউন্ট:',
  'subscription.limitHorizon': 'আগাম নির্ধারণ:',
  'subscription.postsRemaining': 'এ মাসে বাকি পোস্ট:',

  // ─── Dashboard: payments tab ───────────────────────────────────────────────
  'payments.signInPrompt': 'আপনার পেমেন্ট দেখতে সাইন ইন করুন।',
  'payments.emptyTitle': 'এখনো কোনো পেমেন্ট নেই',
  'payments.emptyBody': 'আপগ্রেড করলে আপনার কেনাকাটা এখানে দেখা যাবে।',
  'payments.viewPlans': 'প্ল্যান দেখুন',

  // ─── Pricing ───────────────────────────────────────────────────────────────
  'pricing.badge': 'সাবস্ক্রিপশন প্ল্যান',
  'pricing.title': 'আপনার প্ল্যান বেছে নিন',
  'pricing.subtitle':
    'AI-লিখিত কনটেন্ট — আপনার সংযুক্ত প্রতিটি চ্যানেলে নির্ধারিত সময়ে প্রকাশিত।',
  'pricing.perMonth': 'প্রতি মাসে',
  'pricing.days14': '১৪ দিন',
  'pricing.contactUs': 'যোগাযোগ করুন',
  'pricing.custom': 'কাস্টম',
  'pricing.badgeFree': '১৪ দিন ফ্রি',
  'pricing.badgeBest': 'সেরা মূল্য',
  'pricing.planFreeTrial': 'ফ্রি ট্রায়াল',
  'pricing.planStarter': 'স্টার্টার',
  'pricing.planGrowth': 'গ্রোথ',
  'pricing.planAgency': 'এজেন্সি',
  'pricing.planEnterprise': 'এন্টারপ্রাইজ',
  'pricing.planLegacyProTrial': 'প্রো ট্রায়াল',
  'pricing.planLegacyPro': 'প্রো',
  'pricing.ctaFreeTrial': 'ফ্রি ট্রায়াল শুরু করুন',
  'pricing.ctaStarter': 'স্টার্টার নিন',
  'pricing.ctaGrowth': 'গ্রোথ নিন',
  'pricing.ctaAgency': 'এজেন্সি নিন',
  'pricing.ctaEnterprise': 'সেলসে যোগাযোগ করুন',
  'pricing.featBrands': '{count} টি ব্র্যান্ড',
  'pricing.featBrandsPlural': '{count} টি ব্র্যান্ড',
  'pricing.featAccounts': '{count} টি সংযুক্ত অ্যাকাউন্ট',
  'pricing.featPosts': 'মাসে {count} টি পোস্ট',
  'pricing.featGenerations': 'মাসে {count} টি AI জেনারেশন',
  'pricing.featStorage': '{amount} মিডিয়া স্টোরেজ',
  'pricing.featPlatforms': '{platforms} এ প্রকাশ',
  'pricing.featScheduleHorizon': '{days} দিন আগাম নির্ধারণ',
  'pricing.featApprovals': 'অনুমোদন প্রক্রিয়া',
  'pricing.featAnalytics': 'পোস্ট বিশ্লেষণ',
  'pricing.featWorkspaces': 'ক্লায়েন্ট ওয়ার্কস্পেস',
  'pricing.featSupport': 'ডেডিকেটেড সাপোর্ট',
  'pricing.featCustomVolume': 'কাস্টম পরিমাণ',
  'pricing.featMultiUser': 'একাধিক ব্যবহারকারী',
  'pricing.featBranding': 'কাস্টম ব্র্যান্ডিং',
  'pricing.featInvoice': 'ইনভয়েস বিলিং',
  'pricing.payFailed': 'পেমেন্ট ব্যর্থ হয়েছে। আবার চেষ্টা করুন।',

  // ─── Payment result ────────────────────────────────────────────────────────
  'payment.successTitle': 'পেমেন্ট সফল!',
  'payment.successBody': 'আপনার সাবস্ক্রিপশন এখন সক্রিয়। এখনই কনটেন্ট পরিকল্পনা শুরু করুন।',
  'payment.plan': 'প্ল্যান',
  'payment.postsPerMonth': 'পোস্ট/মাস',
  'payment.expires': 'মেয়াদ শেষ',
  'payment.activating': 'সাবস্ক্রিপশন সক্রিয় হচ্ছে…',
  'payment.tranId': 'ট্রানজেকশন আইডি: {id}',
  'payment.goToDashboard': 'ড্যাশবোর্ডে যান',
  'payment.receipt': 'আপনার নিবন্ধিত ইমেইলে একটি রসিদ পাঠানো হয়েছে।',
  'payment.failTitle': 'পেমেন্ট ব্যর্থ',
  'payment.failBody': 'আপনার পেমেন্ট প্রক্রিয়া করা যায়নি। কোনো চার্জ কাটা হয়নি।',
  'payment.tryAgain': 'আবার চেষ্টা করুন',
  'payment.backToDashboard': 'ড্যাশবোর্ডে ফিরুন',
  'payment.cancelTitle': 'পেমেন্ট বাতিল',
  'payment.cancelBody': 'আপনি পেমেন্ট বাতিল করেছেন। আপনার বর্তমান প্ল্যান অপরিবর্তিত রয়েছে।',
  'payment.viewPlans': 'প্ল্যান দেখুন',

  // ─── Admin ─────────────────────────────────────────────────────────────────
  'admin.searchUsers': 'নাম বা ইমেইল দিয়ে খুঁজুন',
  'admin.colUser': 'ব্যবহারকারী',
  'admin.colPlan': 'প্ল্যান',
  'admin.colSignedUp': 'নিবন্ধন',
  'admin.colLastSeen': 'সর্বশেষ সক্রিয়',
  'admin.colGenerations': 'জেনারেশন',
  'admin.roleBadge': 'প্রশাসক',
  'admin.viewActivity': 'কার্যক্রম দেখুন',
  'admin.colAccount': 'অ্যাকাউন্ট',
  'admin.accountOk': 'সক্রিয়',
  'admin.bannedBadge': 'স্থগিত',
  'admin.bannedUntil': '{date} পর্যন্ত',
  'admin.ban': 'স্থগিত করুন',
  'admin.unban': 'প্রত্যাহার',
  'admin.banTitle': 'এই অ্যাকাউন্ট স্থগিত করুন',
  'admin.banDescription':
    '{user} কে সাইন আউট করা হবে এবং API ও পুনরায় সাইন ইন করা থেকে বিরত রাখা হবে।',
  'admin.banDuration': 'মেয়াদ',
  'admin.banDays': '{days} দিন',
  'admin.banIndefinite': 'অনির্দিষ্টকাল',
  'admin.banReason': 'কারণ',
  'admin.banReasonPlaceholder': 'যেমন: জেনারেশন এন্ডপয়েন্টের স্বয়ংক্রিয় অপব্যবহার',
  'admin.banReasonHint': 'অ্যাপ ব্যবহারের প্রতিটি চেষ্টায় ব্যবহারকারীকে দেখানো হবে।',
  'admin.banConfirm': 'অ্যাকাউন্ট স্থগিত করুন',
  'admin.banFailed': 'অ্যাকাউন্টটি স্থগিত করা যায়নি।',
  'admin.unbanFailed': 'স্থগিতাদেশ প্রত্যাহার করা যায়নি।',
  'admin.noUsers': 'এই অনুসন্ধানে কোনো ব্যবহারকারী মেলেনি।',
  'admin.noActivity': 'এখনো কিছু নথিভুক্ত হয়নি। মানুষ পণ্যটি ব্যবহার করলে কার্যক্রম এখানে দেখা যাবে।',
  'admin.filterAll': 'সব ধরনের ইভেন্ট',
  'admin.filterClear': 'ফিল্টার মুছুন',
  'admin.filteredToUser': 'শুধু একজন ব্যবহারকারীর কার্যক্রম দেখানো হচ্ছে।',
  'admin.showing': '{total} টির মধ্যে {from}–{to} দেখানো হচ্ছে',
  'admin.never': 'কখনো নয়',
  'admin.statTotalUsers': 'মোট ব্যবহারকারী',
  'admin.statNewUsers': '{days} দিনে নতুন',
  'admin.statActiveSubs': 'সক্রিয় সাবস্ক্রিপশন',
  'admin.statGenerations': 'তৈরি কনটেন্ট',
  'admin.statPostsPublished': 'প্রকাশিত পোস্ট',
  'admin.statConnectedAccounts': 'সংযুক্ত অ্যাকাউন্ট',
  'admin.statNeedsReauth': 'পুনঃসংযোগ প্রয়োজন',
  'admin.statRevenue': 'আয় ({days}দি)',
  'admin.perDayTitle': 'প্রতিদিনের জেনারেশন',
  'admin.postsPerDayTitle': 'প্রতিদিন প্রকাশিত পোস্ট',
  'admin.planDistTitle': 'সক্রিয় প্ল্যান',
  'admin.platformDistTitle': 'সংযুক্ত প্ল্যাটফর্ম',
  'admin.eventBreakdownTitle': 'ধরন অনুযায়ী ইভেন্ট',
  'admin.rangeDays': 'সর্বশেষ {days} দিন',
  'admin.noStats': 'এখনো যথেষ্ট তথ্য নেই।',

  // ─── Admin: activity event labels ──────────────────────────────────────────
  'activity.user_signed_up': 'নিবন্ধন করেছেন',
  'activity.user_signed_in': 'সাইন ইন করেছেন',
  'activity.content_generated': 'কনটেন্ট তৈরি করেছেন',
  'activity.generation_blocked': 'জেনারেশন বাধাগ্রস্ত',
  'activity.generation_failed': 'জেনারেশন ব্যর্থ',
  'activity.brand_created': 'ব্র্যান্ড তৈরি করেছেন',
  'activity.account_connected': 'অ্যাকাউন্ট সংযুক্ত করেছেন',
  'activity.account_disconnected': 'অ্যাকাউন্ট বিচ্ছিন্ন করেছেন',
  'activity.account_reauth_required': 'অ্যাকাউন্ট পুনঃসংযোগ প্রয়োজন',
  'activity.post_scheduled': 'পোস্ট নির্ধারণ করেছেন',
  'activity.post_published': 'পোস্ট প্রকাশিত',
  'activity.post_failed': 'পোস্ট প্রকাশ ব্যর্থ',
  'activity.post_approved': 'পোস্ট অনুমোদন করেছেন',
  'activity.post_rejected': 'পোস্ট প্রত্যাখ্যান করেছেন',
  'activity.token_refresh_failed': 'টোকেন রিফ্রেশ ব্যর্থ',
  'activity.payment_initiated': 'পেমেন্ট শুরু করেছেন',
  'activity.payment_completed': 'পেমেন্ট সম্পন্ন',
  'activity.payment_failed': 'পেমেন্ট ব্যর্থ',
  'activity.subscription_activated': 'প্ল্যান সক্রিয়',
  'activity.subscription_cancelled': 'প্ল্যান বাতিল',
  'activity.user_banned': 'অ্যাকাউন্ট স্থগিত',
  'activity.user_unbanned': 'স্থগিতাদেশ প্রত্যাহার',
  'activity.question_generated': 'প্রশ্নপত্র তৈরি করেছেন (পুরনো)',
};
